import type { BarTimeGranularity } from './barChartData'

export interface SeriesPoint {
  /** ISO date at day resolution (e.g. `2026-06-23`). */
  date: string
  value: number | null
}

/** Normalise any ISO date to UTC midnight of that calendar day. */
function toUtcDay(iso: string): Date {
  const d = new Date(iso)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

/** Start of the bucket a day falls in: the day itself, its Monday, or the 1st. */
function bucketStart(day: Date, granularity: BarTimeGranularity): Date {
  if (granularity === 'month') {
    return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1))
  }
  // Week → ISO week start (Monday). getUTCDay: 0 = Sunday … 6 = Saturday.
  const daysSinceMonday = (day.getUTCDay() + 6) % 7
  const monday = new Date(day)
  monday.setUTCDate(monday.getUTCDate() - daysSinceMonday)
  return monday
}

function isoDay(day: Date): string {
  return day.toISOString().slice(0, 10)
}

/**
 * Sum a daily count series into day / week / month buckets, keyed by the bucket
 * start (the day itself, its Monday, or the 1st-of-month, all UTC to match how
 * points are stored). Output is chronological, one point per bucket. `day` is a
 * normalising passthrough (UTC-day dates, same values).
 *
 * For count metrics (e.g. posts) only — a null day counts as 0. Never use for
 * cumulative metrics like followers, which must not be summed across a bucket.
 * Pure.
 */
export function bucketTimeSeries(
  points: SeriesPoint[],
  granularity: BarTimeGranularity,
): SeriesPoint[] {
  const sorted = [...points].toSorted(
    (a, b) => toUtcDay(a.date).getTime() - toUtcDay(b.date).getTime(),
  )

  if (granularity === 'day') {
    return sorted.map((p) => ({
      date: isoDay(toUtcDay(p.date)),
      value: p.value,
    }))
  }

  // Preserve first-seen (chronological) order while summing into buckets.
  const order: string[] = []
  const totals = new Map<string, number>()
  for (const point of sorted) {
    const key = isoDay(bucketStart(toUtcDay(point.date), granularity))
    if (totals.has(key)) {
      totals.set(key, (totals.get(key) ?? 0) + (point.value ?? 0))
    } else {
      totals.set(key, point.value ?? 0)
      order.push(key)
    }
  }

  return order.map((key) => ({ date: key, value: totals.get(key) ?? 0 }))
}
